import {
  CitriniBasketListItem,
  CitriniBasketWithDetails,
  CitriniFetchInfoItem,
  CitriniFetchInfoParams,
  CitriniMappingItem,
  CitriniMappingsParams,
  CitriniPortfolioListItem,
  CitriniPortfolioWithDetails,
  CitriniSyncInfoItem,
  CitriniSyncInfoParams,
  PagedResponse,
} from "../types/citrini";
import httpService from "./httpService";

const BASE = "/v3/citrini";

function buildQuery(params: object): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (entries.length === 0) return "";
  return "?" + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join("&");
}

class CitriniService {
  // -- Fetch Info --

  async listFetchInfo(params: CitriniFetchInfoParams = {}): Promise<PagedResponse<CitriniFetchInfoItem>> {
    const qs = buildQuery(params);
    const response = await httpService.get<PagedResponse<CitriniFetchInfoItem>>(`${BASE}/fetch/info${qs}`, {
      requiresAuth: true,
    });
    return response.data;
  }

  async updateFetchInfo(id: number, data: unknown): Promise<CitriniFetchInfoItem> {
    const response = await httpService.put<CitriniFetchInfoItem>(`${BASE}/fetch/info/${id}`, { data }, {
      requiresAuth: true,
    });
    return response.data;
  }

  // -- Fetch Baskets --

  async listBasketsByFetchInfoId(citriniFetchInfoId: number): Promise<CitriniBasketListItem[]> {
    const qs = buildQuery({ citriniFetchInfoId });
    const response = await httpService.get<CitriniBasketListItem[]>(`${BASE}/fetch/baskets${qs}`, {
      requiresAuth: true,
    });
    return response.data;
  }

  async listLatestBaskets(): Promise<CitriniBasketListItem[]> {
    const response = await httpService.get<CitriniBasketListItem[]>(`${BASE}/fetch/baskets/latest`, {
      requiresAuth: true,
    });
    return response.data;
  }

  async getLatestBasketByCitriniId(citriniId: string): Promise<CitriniBasketListItem> {
    const response = await httpService.get<CitriniBasketListItem>(
      `${BASE}/fetch/baskets/latest/${encodeURIComponent(citriniId)}`,
      { requiresAuth: true }
    );
    return response.data;
  }

  async getBasketById(id: number): Promise<CitriniBasketWithDetails> {
    const response = await httpService.get<CitriniBasketWithDetails>(`${BASE}/fetch/baskets/${id}`, {
      requiresAuth: true,
    });
    return response.data;
  }

  // -- Fetch Portfolios --

  async listPortfoliosByFetchInfoId(citriniFetchInfoId: number): Promise<CitriniPortfolioListItem[]> {
    const qs = buildQuery({ citriniFetchInfoId });
    const response = await httpService.get<CitriniPortfolioListItem[]>(`${BASE}/fetch/portfolios${qs}`, {
      requiresAuth: true,
    });
    return response.data;
  }

  async listLatestPortfolios(): Promise<CitriniPortfolioListItem[]> {
    const response = await httpService.get<CitriniPortfolioListItem[]>(`${BASE}/fetch/portfolios/latest`, {
      requiresAuth: true,
    });
    return response.data;
  }

  async getLatestPortfolioByCitriniId(citriniId: string): Promise<CitriniPortfolioListItem> {
    const response = await httpService.get<CitriniPortfolioListItem>(
      `${BASE}/fetch/portfolios/latest/${encodeURIComponent(citriniId)}`,
      { requiresAuth: true }
    );
    return response.data;
  }

  async getPortfolioById(id: number): Promise<CitriniPortfolioWithDetails> {
    const response = await httpService.get<CitriniPortfolioWithDetails>(`${BASE}/fetch/portfolios/${id}`, {
      requiresAuth: true,
    });
    return response.data;
  }

  // -- Sync Info --

  async listSyncInfo(params: CitriniSyncInfoParams = {}): Promise<PagedResponse<CitriniSyncInfoItem>> {
    const qs = buildQuery(params);
    const response = await httpService.get<PagedResponse<CitriniSyncInfoItem>>(`${BASE}/sync/info${qs}`, {
      requiresAuth: true,
    });
    return response.data;
  }

  async getSyncInfoById(id: number): Promise<CitriniSyncInfoItem> {
    const response = await httpService.get<CitriniSyncInfoItem>(`${BASE}/sync/info/${id}`, {
      requiresAuth: true,
    });
    return response.data;
  }

  // -- Mappings --

  async listMappings(params: CitriniMappingsParams = {}): Promise<PagedResponse<CitriniMappingItem>> {
    const qs = buildQuery(params);
    const response = await httpService.get<PagedResponse<CitriniMappingItem>>(`${BASE}/mappings${qs}`, {
      requiresAuth: true,
    });
    return response.data;
  }

  async getMappingById(id: number): Promise<CitriniMappingItem> {
    const response = await httpService.get<CitriniMappingItem>(`${BASE}/mappings/${id}`, {
      requiresAuth: true,
    });
    return response.data;
  }

  async deleteMapping(id: number): Promise<{ message: string }> {
    const response = await httpService.delete<{ message: string }>(`${BASE}/mappings/${id}`, {
      requiresAuth: true,
    });
    return response.data;
  }
}

const citriniService = new CitriniService();
export default citriniService;
